import React from 'react';
import PropTypes from 'prop-types';
import {
  LuArrowRight,
  LuBan,
  LuBell,
  LuCheck,
  LuClock,
  LuFileSpreadsheet,
  LuFileUp,
  LuFolderKanban,
  LuListChecks,
  LuLoaderCircle,
  LuSearch,
  LuTriangleAlert,
} from 'react-icons/lu';
import Badge from '../../../../components/ui/Badge';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { abs } from '../engine/stage';
import { Abs, Btn, Dialog, DialogIcon, Field, GLASS_CHIP, Segmented, TILE } from './kit';

// ── Görevlerim ──────────────────────────────────────────────────────────
export const TASKS = {
  dialog: { x: 280, y: 40, w: 720, h: 640 },
  stats: { x: 300, y: 128, w: 680, h: 62 },
  stat: (index) => ({ x: 300 + index * 172, y: 128, w: 162, h: 62 }),
  filter: { x: 300, y: 206, w: 296, h: 36 },
  filterWidths: [86, 116, 78],
  card: (index, expanded) => {
    const y = 258 + index * 74 + (expanded !== null && expanded !== undefined && index > expanded ? 132 : 0);
    return { x: 300, y, w: 680, h: index === expanded ? 196 : 64 };
  },
  statusPill: (index) => ({ x: 320 + index * 132, y: 258 + 154, w: 124, h: 30 }),
};

const STATUS_ORDER = ['pending', 'in-progress', 'completed', 'cancelled'];
const STATUS_STYLE = {
  pending: { tone: 'neutral', icon: LuClock },
  'in-progress': { tone: 'info', icon: LuLoaderCircle },
  completed: { tone: 'success', icon: LuCheck },
  cancelled: { tone: 'danger', icon: LuBan },
};
const PRIORITY_BAR = { high: 'tw-bg-rose-500', medium: 'tw-bg-amber-400', low: 'tw-bg-emerald-500' };

export const TaskList = ({ state }) => {
  const { t } = useTranslation();
  const firstStatus = state.status || 'in-progress';
  const tasks = [
    { key: 'task1', priority: 'high', status: firstStatus, overdue: firstStatus !== 'completed', due: '08.10.2026' },
    { key: 'task2', priority: 'medium', status: 'pending', isNew: !state.seen, due: '18.10.2026' },
    { key: 'task3', priority: 'low', status: 'pending', due: '30.10.2026' },
  ];
  const done = firstStatus === 'completed';
  const stats = [
    { key: 'pending', value: 2, tone: '' },
    { key: 'inProgress', value: done ? 0 : 1, tone: '' },
    { key: 'completed', value: done ? 5 : 4, tone: '' },
    { key: 'overdue', value: done ? 0 : 1, tone: done ? '' : 'tw-border-rose-400/50 tw-bg-rose-50 dark:tw-bg-rose-500/10' },
  ];
  return (
    <>
      {stats.map((stat, index) => (
        <Abs key={stat.key} rect={TASKS.stat(index)} className={`tw-px-4 tw-py-2.5 ${TILE} ${stat.tone}`}>
          <p className="tw-text-2xl tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{stat.value}</p>
          <p className="tw-text-xs tw-text-slate-500">{t(`tasks.stats.${stat.key}`)}</p>
        </Abs>
      ))}
      <Segmented
        rect={TASKS.filter}
        value={state.filter || 'active'}
        options={['active', 'completed', 'all'].map((key, index) => ({ value: key, label: t(`tasks.filters.${key}`), w: TASKS.filterWidths[index] }))}
      />
      {tasks.map((task, index) => {
        const rect = TASKS.card(index, state.expanded);
        const style = STATUS_STYLE[task.status];
        const StatusIcon = style.icon;
        return (
          <Abs key={task.key} rect={rect} className={`tw-overflow-hidden tw-rounded-2xl tw-border tw-bg-white tw-transition-all tw-duration-300 dark:tw-bg-white/[0.03] ${task.overdue ? 'tw-border-rose-300 dark:tw-border-rose-400/40' : 'tw-border-slate-200 dark:tw-border-white/10'} ${task.isNew ? 'tw-ring-2 tw-ring-teal-400/50' : ''}`}>
            <span className={`tw-absolute tw-inset-y-0 tw-left-0 tw-w-1.5 ${PRIORITY_BAR[task.priority]}`} />
            <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-pt-3">
              <p className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t(`userGuide.stage.${task.key}`)}</p>
              {task.isNew && <Badge tone="info">{t('tasks.newBadge')}</Badge>}
              {task.overdue && <Badge tone="danger">{t('tasks.overdue')}</Badge>}
              <Badge tone={style.tone}>
                <StatusIcon aria-hidden="true" className="tw-h-3 tw-w-3" />
                {t(`tasks.status.${task.status}`)}
              </Badge>
            </div>
            <p className="tw-px-5 tw-pt-1 tw-text-xs tw-text-slate-500">
              {t(`tasks.priority.${task.priority}`)} · <span className={task.overdue ? 'tw-font-semibold tw-text-rose-600' : ''}>{task.due}</span>
            </p>
            {index === state.expanded && (
              <div className="tw-px-5 tw-pt-3">
                <p className="tw-text-[13px] tw-text-slate-600 dark:tw-text-slate-300">{t('userGuide.stage.task1Desc')}</p>
                <p className="tw-mt-1 tw-text-xs tw-text-slate-500">{t('tasks.assignees')}: Ayşe Kaya</p>
                <p className="tw-mt-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{t('tasks.changeStatus')}</p>
              </div>
            )}
          </Abs>
        );
      })}
      {state.expanded === 0 && STATUS_ORDER.map((key, index) => {
        const active = key === firstStatus;
        const Icon = STATUS_STYLE[key].icon;
        return (
          <span key={key} style={abs(TASKS.statusPill(index))} className={`tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-text-xs tw-font-semibold tw-transition-colors tw-duration-300 ${active ? 'tw-bg-teal-500 tw-text-white' : `${GLASS_CHIP} tw-text-slate-600 dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none dark:tw-text-slate-300`}`}>
            <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {t(`tasks.status.${key}`)}
          </span>
        );
      })}
    </>
  );
};

TaskList.propTypes = { state: PropTypes.object.isRequired };

export const TasksDialog = ({ state, appear }) => {
  const { t } = useTranslation();
  const openCount = state.status === 'completed' ? 2 : 3;
  return (
    <Dialog rect={TASKS.dialog} appear={appear} title={t('tasks.title')} subtitle={t('tasks.subtitle', { count: openCount })} icon={<DialogIcon icon={LuListChecks} gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" />}>
      <TaskList state={state} />
    </Dialog>
  );
};

TasksDialog.propTypes = { state: PropTypes.object.isRequired, appear: PropTypes.number };

// ── Projeler ────────────────────────────────────────────────────────────
export const PROJ = {
  dialog: { x: 88, y: 36, w: 1104, h: 648 },
  toggle: { x: 736, y: 52, w: 240, h: 36 },
  toggleWidths: [112, 116],
  export: { x: 984, y: 52, w: 152, h: 36 },
  search: { x: 108, y: 124, w: 520, h: 40 },
  filters: { x: 640, y: 124, w: 380, h: 40 },
  table: { x: 108, y: 184, w: 1064, h: 200 },
  row: (index) => ({ x: 108, y: 222 + index * 54, w: 1064, h: 54 }),
  detail: (index) => ({ x: 1066, y: 222 + index * 54 + 11, w: 92, h: 32 }),
};

const COLUMNS = [
  { key: 'code', x: 124, w: 120 },
  { key: 'protocol', x: 250, w: 150 },
  { key: 'date', x: 410, w: 110 },
  { key: 'name', x: 530, w: 310 },
  { key: 'assignment', x: 850, w: 100 },
  { key: 'status', x: 955, w: 100 },
  { key: 'action', x: 1066, w: 92 },
];
const PROJECT_ROWS = [
  { code: 'ONKO-301', protocol: 'PRT-2025-114', date: '12.01.2025', name: 'p1', assignment: 'active', status: 'active' },
  { code: 'KARDİYO-12', protocol: 'PRT-2024-088', date: '03.06.2024', name: 'p2', assignment: 'active', status: 'active' },
  { code: 'NÖRO-7', protocol: 'PRT-2023-041', date: '20.09.2023', name: 'p3', assignment: 'passive', status: 'active' },
];

export const ProjectsDialog = ({ state, appear }) => {
  const { t } = useTranslation();
  const statusBadge = (value) => <Badge tone={value === 'active' ? 'success' : 'neutral'}>{t(`common.${value}`)}</Badge>;
  return (
    <Dialog rect={PROJ.dialog} appear={appear} title={t('projects.title')} subtitle="Ayşe Kaya" titleRight={400} icon={<DialogIcon icon={LuFolderKanban} gradient="tw-from-violet-500 tw-to-fuchsia-500 tw-shadow-violet-500/30" />}>
      <Segmented
        rect={PROJ.toggle}
        value={state.all ? 'all' : 'assigned'}
        small
        options={[{ value: 'assigned', label: t('projects.assignedOnly'), w: PROJ.toggleWidths[0] }, { value: 'all', label: t('projects.allProjects'), w: PROJ.toggleWidths[1] }]}
      />
      <Btn rect={PROJ.export} variant="success" icon={LuFileSpreadsheet} pressed={state.exportPressed}>{t('projects.export')}</Btn>
      <Field rect={PROJ.search} value={state.search} placeholder={t('projects.searchPlaceholder')} prefix={<LuSearch aria-hidden="true" className="tw-h-4 tw-w-4" />} focused={state.searchFocus} />
      <Field rect={{ ...PROJ.filters, w: 184 }} value={`${t('projects.assignmentStatus')}: ${t('common.all')}`} select />
      <Field rect={{ ...PROJ.filters, x: PROJ.filters.x + 196, w: 184 }} value={`${t('projects.projectStatus')}: ${t('common.all')}`} select />
      <span style={{ left: 1034, top: 136 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-500">{t('projects.count', { count: 3 })}</span>
      <Abs rect={{ ...PROJ.table, y: 184, h: 38 }} className="tw-rounded-t-xl tw-bg-slate-50 dark:tw-bg-white/[0.04]" />
      {COLUMNS.map((column) => (
        <span key={column.key} style={{ left: column.x, top: 196, width: column.w }} className="tw-absolute tw-truncate tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">{t(`projects.columns.${column.key}`)}</span>
      ))}
      {PROJECT_ROWS.map((row, index) => {
        const rect = PROJ.row(index);
        const passive = row.assignment === 'passive';
        return (
          <React.Fragment key={row.code}>
            <Abs rect={rect} className={`tw-border-b tw-border-slate-100 dark:tw-border-white/5 ${passive ? 'tw-opacity-60' : ''} ${state.rowHover === index ? 'tw-bg-teal-500/[0.06]' : ''}`} />
            <span style={{ left: COLUMNS[0].x, top: rect.y + 18 }} className={`tw-absolute tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white ${passive ? 'tw-opacity-60' : ''}`}>{row.code}</span>
            <span style={{ left: COLUMNS[1].x, top: rect.y + 18 }} className="tw-absolute tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{row.protocol}</span>
            <span style={{ left: COLUMNS[2].x, top: rect.y + 18 }} className="tw-absolute tw-text-sm tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{row.date}</span>
            <span style={{ left: COLUMNS[3].x, top: rect.y + 18, width: COLUMNS[3].w }} className="tw-absolute tw-truncate tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">{t(`userGuide.stage.${row.name}`)}</span>
            <span style={{ left: COLUMNS[4].x, top: rect.y + 15 }} className="tw-absolute">{statusBadge(row.assignment)}</span>
            <span style={{ left: COLUMNS[5].x, top: rect.y + 15 }} className="tw-absolute">{statusBadge(row.status)}</span>
            <Btn rect={PROJ.detail(index)} pressed={state.detailPressed && index === 0}>{t('projects.detail')}</Btn>
          </React.Fragment>
        );
      })}
    </Dialog>
  );
};

ProjectsDialog.propTypes = { state: PropTypes.object.isRequired, appear: PropTypes.number };

export const PROJ_DETAIL = {
  dialog: { x: 200, y: 64, w: 880, h: 600 },
  facts: { x: 220, y: 148, w: 840, h: 132 },
  centers: { x: 220, y: 300, w: 840, h: 118 },
  summary: { x: 220, y: 436, w: 840, h: 210 },
};

export const ProjectDetailDialog = ({ appear }) => {
  const { t } = useTranslation();
  const facts = [
    ['protocol', 'PRT-2025-114'],
    ['customer', 'ICON'],
    ['startDate', '12.01.2025'],
    ['endDate', '31.12.2027'],
    ['projectStatus', t('common.active')],
    ['assignmentStatus', t('common.active')],
  ];
  const statuses = ['planned', 'screened', 'screenFail', 'enrolled', 'active', 'onMedication', 'completed', 'withdrawn'];
  const dots = ['tw-bg-amber-400', 'tw-bg-blue-500', 'tw-bg-rose-500', 'tw-bg-violet-500', 'tw-bg-emerald-500', 'tw-bg-cyan-500', 'tw-bg-slate-400', 'tw-bg-red-800'];
  const counts = [1, 2, 1, 3, 4, 2, 1, 0];
  return (
    <Dialog rect={PROJ_DETAIL.dialog} appear={appear} z={40} title="ONKO-301" subtitle={t('userGuide.stage.p1')} icon={<DialogIcon icon={LuFolderKanban} gradient="tw-from-violet-500 tw-to-fuchsia-500 tw-shadow-violet-500/30" />}>
      {facts.map(([key, value], index) => (
        <Abs key={key} rect={{ x: 220 + (index % 3) * 284, y: 148 + Math.floor(index / 3) * 68, w: 272, h: 58 }} className={`tw-px-4 tw-py-2 ${TILE}`}>
          <p className="tw-text-[11px] tw-text-slate-500">{t(`projectDetail.${key}`)}</p>
          <p className="tw-mt-0.5 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{value}</p>
        </Abs>
      ))}
      <span style={{ left: 222, top: 296 }} className="tw-absolute tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{t('projectDetail.centersTitle')}</span>
      <Abs rect={{ x: 220, y: 322, w: 840, h: 86 }} className={`tw-overflow-hidden ${TILE}`}>
        <div className="tw-grid tw-grid-cols-5 tw-bg-slate-50 tw-px-4 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500 dark:tw-bg-white/[0.04]">
          {['center', 'role', 'workerType', 'blinding', 'status'].map((key) => <span key={key}>{t(`projectDetail.columns.${key}`)}</span>)}
        </div>
        <div className="tw-grid tw-grid-cols-5 tw-items-center tw-px-4 tw-py-3 tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">
          <span>{t('userGuide.stage.center')}</span>
          <span>Study Coordinator</span>
          <span><Badge tone="info">Primer</Badge></span>
          <span><Badge tone="accent">Blinded</Badge></span>
          <span><Badge tone="success">{t('common.active')}</Badge></span>
        </div>
      </Abs>
      <span style={{ left: 222, top: 430 }} className="tw-absolute tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
        {t('projectDetail.summaryTitle')} <span className="tw-font-normal tw-text-slate-400">· {t('projectDetail.summaryHint')}</span>
      </span>
      {['patients', 'homeVisitPatients', 'visits'].map((key, index) => (
        <Abs key={key} rect={{ x: 220 + index * 284, y: 458, w: 272, h: 58 }} className={`tw-px-4 tw-py-2 ${TILE}`}>
          <p className="tw-text-xl tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{[14, 3, 62][index]}</p>
          <p className="tw-text-[11px] tw-text-slate-500">{t(`projectDetail.${key}`)}</p>
        </Abs>
      ))}
      <div style={{ left: 220, top: 530, width: 840 }} className="tw-absolute tw-grid tw-grid-cols-4 tw-gap-2">
        {statuses.map((key, index) => (
          <span key={key} className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-[12px] tw-text-slate-600 dark:tw-text-slate-300 ${TILE}`}>
            <span className={`tw-h-2 tw-w-2 tw-rounded-full ${dots[index]}`} />
            <span className="tw-flex-1 tw-truncate">{t(`projectDetail.patientStatus.${key}`)}</span>
            <span className="tw-font-semibold tw-tabular-nums">{counts[index]}</span>
          </span>
        ))}
      </div>
    </Dialog>
  );
};

ProjectDetailDialog.propTypes = { appear: PropTypes.number };

// ── Girişteki hatırlatma ────────────────────────────────────────────────
export const REMINDER = {
  dialog: { x: 352, y: 80, w: 576, h: 584 },
  sops: { x: 376, y: 164, w: 528, h: 182 },
  sopAction: { x: 756, y: 302, w: 132, h: 34 },
  docs: { x: 376, y: 358, w: 528, h: 182 },
  docAction: { x: 740, y: 496, w: 148, h: 34 },
  later: { x: 800, y: 614, w: 112, h: 36 },
};

export const ReminderDialog = ({ state, appear }) => {
  const { t } = useTranslation();
  const Section = ({ rect, tone, icon: Icon, title, description, count, children }) => (
    <Abs rect={rect} className={`tw-overflow-hidden tw-rounded-2xl tw-border tw-border-l-4 tw-bg-white tw-px-4 tw-py-3 dark:tw-bg-white/[0.03] ${tone === 'rose' ? 'tw-border-rose-400/60' : 'tw-border-amber-400/70'} tw-border-y-slate-200 tw-border-r-slate-200 dark:tw-border-y-white/10 dark:tw-border-r-white/10`}>
      <div className="tw-flex tw-items-start tw-gap-3">
        <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tone === 'rose' ? 'tw-bg-rose-500/10 tw-text-rose-600' : 'tw-bg-amber-500/15 tw-text-amber-600'}`}>
          <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
        </span>
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            {title}
            <span className={`tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-px-1 tw-text-[11px] tw-font-bold tw-text-white ${tone === 'rose' ? 'tw-bg-rose-500' : 'tw-bg-amber-500'}`}>{count}</span>
          </p>
          <p className="tw-text-xs tw-text-slate-500">{description}</p>
          <div className="tw-mt-2 tw-space-y-1.5">{children}</div>
        </div>
      </div>
    </Abs>
  );
  Section.propTypes = { rect: PropTypes.object, tone: PropTypes.string, icon: PropTypes.elementType, title: PropTypes.node, description: PropTypes.node, count: PropTypes.number, children: PropTypes.node };
  const Row = ({ children, badge }) => (
    <span className="tw-flex tw-h-8 tw-items-center tw-justify-between tw-rounded-lg tw-bg-slate-50 tw-px-3 tw-text-[13px] tw-text-slate-700 dark:tw-bg-white/[0.04] dark:tw-text-slate-200">
      {children}
      {badge}
    </span>
  );
  Row.propTypes = { children: PropTypes.node, badge: PropTypes.node };
  return (
    <Dialog rect={REMINDER.dialog} appear={appear} footer title={t('loginReminders.title')} subtitle={t('loginReminders.subtitle', { count: 2 })} icon={<DialogIcon icon={LuBell} gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" />}>
      <Section rect={REMINDER.sops} tone="rose" icon={LuTriangleAlert} title={t('loginReminders.sops.title')} description={t('loginReminders.sops.description')} count={2}>
        <Row>SOP-014 {t('userGuide.stage.sop1')}</Row>
        <Row>SOP-022 {t('userGuide.stage.sop2')}</Row>
      </Section>
      <Btn rect={REMINDER.sopAction} variant="danger" pressed={state.press === 'sop'}>
        {t('loginReminders.sops.action')}
      </Btn>
      <Section rect={REMINDER.docs} tone="amber" icon={LuFileUp} title={t('loginReminders.docs.title')} description={t('loginReminders.docs.description')} count={2}>
        <Row badge={<Badge tone="warning">{t('loginReminders.docs.notUploaded')}</Badge>}>{t('profilePage.nav.tabs.gcp')}</Row>
        <Row badge={<Badge tone="warning">{t('loginReminders.docs.notUploaded')}</Badge>}>{t('profilePage.nav.tabs.iata')}</Row>
      </Section>
      <Btn rect={REMINDER.docAction} variant="warning" pressed={state.press === 'docs'}>
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">{t('loginReminders.docs.action')}<LuArrowRight aria-hidden="true" className="tw-h-4 tw-w-4" /></span>
      </Btn>
      <Btn rect={REMINDER.later} variant="ghost">{t('loginReminders.later')}</Btn>
    </Dialog>
  );
};

ReminderDialog.propTypes = { state: PropTypes.object.isRequired, appear: PropTypes.number };
